/**
 * Calculated analytes (ADR 0023): the formula language and its sandboxed evaluator.
 *
 * A formula is arithmetic the laboratory wrote over the codes of its sibling analytes in the same
 * study, e.g. `URE / 2.1428` (BUN) or `COL - HDL - TRIG / 5` (LDL). It is parsed by the small
 * recursive-descent parser below and walked as a tree: no `eval`, no `Function`, no property
 * access, no loops, so a formula can compute a number and nothing else.
 *
 * Grammar (precedence from low to high):
 *
 *   formula    := comparison
 *   comparison := additive [ ("=" | "==" | "<>" | "!=" | "<" | "<=" | ">" | ">=") additive ]
 *   additive   := term { ("+" | "-") term }
 *   term       := unary { ("*" | "/") unary }
 *   unary      := ("-" | "+") unary | power
 *   power      := primary [ "^" unary ]                     (right-associative; -2^2 = -4)
 *   primary    := number | "text" | name | name "(" args ")" | "[" code "]" | "(" formula ")"
 *
 * - number: digits with an optional decimal point (`2.1428`, `.5`). The comma separates
 *   arguments; it is never a decimal mark here.
 * - name: an analyte code of the same study (case-insensitive; `17A` and `12d3` are codes, not
 *   numbers), `edad_anios` (the patient's completed years at the order's date) or `sexo`
 *   ("M" or "F"). `[CODE]` takes any code verbatim, for codes with other characters.
 * - functions: si(condición, sí, no) — only the chosen branch is evaluated —, min, max, abs,
 *   redondear/round(x[, decimales]), raiz/sqrt, ln, log (base 10), exp.
 * - `sexo` is text: it can only be compared with "M" or "F" (`si(sexo = "F", …, …)`).
 *
 * Evaluation never prints 0 or NaN for something it could not compute: a missing input, a
 * division by zero, the root of a negative number or any other non-finite result gives no value.
 */

export const PATIENT_VARIABLES = ["edad_anios", "sexo"] as const;
export type PatientVariable = (typeof PATIENT_VARIABLES)[number];

export interface FormulaPatient {
  /** Completed years at the order's date; null when unknown. */
  edad_anios: number | null;
  sexo: "M" | "F" | null;
}

type CompareOp = "=" | "<>" | "<" | "<=" | ">" | ">=";
type Node =
  | { t: "num"; v: number }
  | { t: "str"; v: string }
  | { t: "code"; code: string }
  | { t: "patient"; name: PatientVariable }
  | { t: "neg"; x: Node }
  | { t: "bin"; op: "+" | "-" | "*" | "/" | "^"; a: Node; b: Node }
  | { t: "cmp"; op: CompareOp; a: Node; b: Node }
  | { t: "call"; fn: FunctionName; args: Node[] };

export interface ParsedFormula {
  /** The source as written (trimmed). */
  source: string;
  /** Analyte codes it reads, upper-cased, deduplicated, in order of appearance. */
  codes: string[];
  /** Patient variables it reads. */
  patient: PatientVariable[];
  /** @internal the syntax tree. */
  ast: Node;
}

export interface FormulaProblem {
  /** es-MX, shown inline in the catalogue editor. */
  message: string;
  /** 0-based character offset in the source. */
  position: number;
}

export type ParseResult = { ok: true; formula: ParsedFormula } | { ok: false; error: FormulaProblem };

/** Upper-case, trimmed: how codes are compared everywhere (the catalogue's `codeKey`). */
export const formulaCodeKey = (code: string): string => code.trim().toUpperCase();

const FUNCTIONS = {
  si: { min: 3, max: 3 },
  min: { min: 1, max: Infinity },
  max: { min: 1, max: Infinity },
  abs: { min: 1, max: 1 },
  redondear: { min: 1, max: 2 },
  raiz: { min: 1, max: 1 },
  ln: { min: 1, max: 1 },
  log: { min: 1, max: 1 },
  exp: { min: 1, max: 1 },
} as const;
type FunctionName = keyof typeof FUNCTIONS;
const ALIASES: Record<string, FunctionName> = { round: "redondear", sqrt: "raiz" };

type Tok = { k: "num"; v: number; p: number } | { k: "id"; v: string; p: number; bracket: boolean } | { k: "str"; v: string; p: number } | { k: "op"; v: string; p: number } | { k: "end"; p: number };

class Fail extends Error {
  constructor(
    message: string,
    readonly position: number,
  ) {
    super(message);
  }
}

const OPS2 = ["<=", ">=", "<>", "!=", "=="];
const OPS1 = "+-*/^(),=<>";

function tokenize(src: string): Tok[] {
  const out: Tok[] = [];
  let i = 0;
  while (i < src.length) {
    const c = src[i]!;
    if (/\s/.test(c)) {
      i++;
    } else if (/[A-Za-z0-9_.]/.test(c)) {
      const start = i;
      while (i < src.length && /[A-Za-z0-9_.]/.test(src[i]!)) i++;
      const word = src.slice(start, i);
      if (/^(\d+\.?\d*|\.\d+)$/.test(word)) out.push({ k: "num", v: Number(word), p: start });
      else if (/^[A-Za-z0-9_]+$/.test(word)) out.push({ k: "id", v: word, p: start, bracket: false });
      else throw new Fail(`«${word}» no es un número ni una clave de analito.`, start);
    } else if (c === "[") {
      const end = src.indexOf("]", i);
      if (end < 0) throw new Fail("Falta cerrar el corchete «]».", i);
      const code = src.slice(i + 1, end).trim();
      if (!code) throw new Fail("Los corchetes están vacíos: escribe una clave de analito.", i);
      out.push({ k: "id", v: code, p: i, bracket: true });
      i = end + 1;
    } else if (c === '"' || c === "'") {
      const end = src.indexOf(c, i + 1);
      if (end < 0) throw new Fail("Falta cerrar las comillas.", i);
      out.push({ k: "str", v: src.slice(i + 1, end), p: i });
      i = end + 1;
    } else {
      const two = src.slice(i, i + 2);
      if (OPS2.includes(two)) {
        out.push({ k: "op", v: two === "!=" ? "<>" : two === "==" ? "=" : two, p: i });
        i += 2;
      } else if (OPS1.includes(c)) {
        out.push({ k: "op", v: c, p: i });
        i++;
      } else {
        throw new Fail(`El símbolo «${c}» no se puede usar en una fórmula.`, i);
      }
    }
  }
  out.push({ k: "end", p: src.length });
  return out;
}

type Ty = "num" | "str";

class Parser {
  private i = 0;
  readonly codes: string[] = [];
  readonly patient: PatientVariable[] = [];
  constructor(private readonly toks: Tok[]) {}

  private peek(): Tok {
    return this.toks[this.i]!;
  }
  private isOp(v: string): boolean {
    const t = this.peek();
    return t.k === "op" && t.v === v;
  }
  private expect(v: string, message: string): void {
    if (!this.isOp(v)) throw new Fail(message, this.peek().p);
    this.i++;
  }

  parse(): Node {
    const n = this.comparison();
    const t = this.peek();
    if (t.k !== "end") throw new Fail(t.k === "op" && t.v === ")" ? "Sobra un paréntesis «)»." : `Falta un operador antes de «${describe(t as Exclude<Tok, { k: "end" }>)}».`, t.p);
    return n;
  }

  private comparison(): Node {
    const a = this.additive();
    const t = this.peek();
    if (t.k === "op" && ["=", "<>", "<", "<=", ">", ">="].includes(t.v)) {
      this.i++;
      const b = this.additive();
      const [ta, tb] = [typeOf(a), typeOf(b)];
      if (ta !== tb) throw new Fail("Solo se comparan números con números, o «sexo» con \"M\" o \"F\".", t.p);
      if (ta === "str" && t.v !== "=" && t.v !== "<>") throw new Fail("«sexo» solo se compara con = o <>.", t.p);
      return { t: "cmp", op: t.v as CompareOp, a, b };
    }
    return a;
  }

  private additive(): Node {
    let a = this.term();
    while (this.isOp("+") || this.isOp("-")) {
      const op = (this.toks[this.i++] as { v: "+" | "-" }).v;
      a = { t: "bin", op, a: num(a), b: num(this.term()) };
    }
    return a;
  }

  private term(): Node {
    let a = this.unary();
    while (this.isOp("*") || this.isOp("/")) {
      const op = (this.toks[this.i++] as { v: "*" | "/" }).v;
      a = { t: "bin", op, a: num(a), b: num(this.unary()) };
    }
    return a;
  }

  private unary(): Node {
    if (this.isOp("-")) {
      this.i++;
      return { t: "neg", x: num(this.unary()) };
    }
    if (this.isOp("+")) {
      this.i++;
      return num(this.unary());
    }
    return this.power();
  }

  private power(): Node {
    const base = this.primary();
    if (this.isOp("^")) {
      this.i++;
      return { t: "bin", op: "^", a: num(base), b: num(this.unary()) };
    }
    return base;
  }

  private primary(): Node {
    const t = this.peek();
    if (t.k === "num") {
      this.i++;
      return { t: "num", v: t.v };
    }
    if (t.k === "str") {
      this.i++;
      const v = t.v.trim().toUpperCase();
      if (v !== "M" && v !== "F") throw new Fail("El único texto que se usa en una fórmula es \"M\" o \"F\", para comparar con «sexo».", t.p);
      return { t: "str", v };
    }
    if (t.k === "id") {
      this.i++;
      if (!t.bracket && this.isOp("(")) return this.call(t);
      const lower = t.v.toLowerCase();
      if (!t.bracket && (PATIENT_VARIABLES as readonly string[]).includes(lower)) {
        const name = lower as PatientVariable;
        if (!this.patient.includes(name)) this.patient.push(name);
        return { t: "patient", name };
      }
      const code = formulaCodeKey(t.v);
      if (!this.codes.includes(code)) this.codes.push(code);
      return { t: "code", code };
    }
    if (this.isOp("(")) {
      this.i++;
      const n = this.comparison();
      this.expect(")", "Falta cerrar un paréntesis «)».");
      return n;
    }
    throw new Fail(t.k === "end" ? "La fórmula está incompleta." : `No se esperaba «${describe(t)}» aquí.`, t.p);
  }

  private call(t: Tok & { k: "id" }): Node {
    const lower = t.v.toLowerCase();
    const fn = (ALIASES[lower] ?? lower) as FunctionName;
    if (!Object.hasOwn(FUNCTIONS, fn)) throw new Fail(`La función «${t.v}» no existe. Se pueden usar: si, min, max, abs, redondear, raiz, ln, log, exp.`, t.p);
    this.i++; // "("
    const args: Node[] = [];
    if (!this.isOp(")")) {
      args.push(this.comparison());
      while (this.isOp(",")) {
        this.i++;
        args.push(this.comparison());
      }
    }
    this.expect(")", `Falta cerrar el paréntesis de «${t.v}».`);
    const { min, max } = FUNCTIONS[fn];
    if (args.length < min || args.length > max) {
      const want = min === max ? `${min}` : max === Infinity ? `al menos ${min}` : `${min} o ${max}`;
      throw new Fail(`«${t.v}» lleva ${want} ${min === 1 && max === 1 ? "valor" : "valores"} entre paréntesis.`, t.p);
    }
    if (fn === "si") {
      if (args[0]!.t !== "cmp") throw new Fail("El primer valor de «si» es una comparación, por ejemplo sexo = \"F\" o edad_anios >= 18.", t.p);
      return { t: "call", fn, args: [args[0]!, num(args[1]!), num(args[2]!)] };
    }
    return { t: "call", fn, args: args.map(num) };
  }
}

function describe(t: Exclude<Tok, { k: "end" }>): string {
  return String(t.v);
}

function typeOf(n: Node): Ty {
  return n.t === "str" || (n.t === "patient" && n.name === "sexo") ? "str" : "num";
}

/** Arithmetic needs numbers: `sexo` and "F" only live inside a comparison. */
function num(n: Node): Node {
  if (typeOf(n) === "str") throw new Fail("«sexo» no es un número: compáralo dentro de si(sexo = \"F\", …, …).", 0);
  if (n.t === "cmp") throw new Fail("Una comparación solo va como primer valor de «si».", 0);
  return n;
}

export function parseFormula(source: string): ParseResult {
  const src = source.trim();
  if (!src) return { ok: false, error: { message: "La fórmula está vacía.", position: 0 } };
  try {
    const p = new Parser(tokenize(src));
    const ast = p.parse();
    if (typeOf(ast) === "str" || ast.t === "cmp") throw new Fail("El resultado de una fórmula es un número, no una comparación.", 0);
    return { ok: true, formula: { source: src, codes: p.codes, patient: p.patient, ast } };
  } catch (e) {
    const f = e as Fail;
    return { ok: false, error: { message: f.message, position: f.position } };
  }
}

/**
 * Parse and check a formula against its study: every code must be a sibling analyte that carries
 * a value, and it may not read itself. A text or option analyte may be an input (the pilot's
 * differential percentages BH17–BH21 are text analytes): its value counts when it reads as a
 * number (`formulaNumber`). Cycles between formulas are checked by `checkStudyFormulas`, which
 * sees the whole study.
 */
export function checkFormula(source: string, ctx: { self: string; valueCodes: readonly string[]; otherCodes?: readonly string[] }): ParseResult {
  const r = parseFormula(source);
  if (!r.ok) return r;
  const self = formulaCodeKey(ctx.self);
  const valued = new Set(ctx.valueCodes.map(formulaCodeKey));
  const other = new Set((ctx.otherCodes ?? []).map(formulaCodeKey));
  for (const code of r.formula.codes) {
    const at = Math.max(0, r.formula.source.toUpperCase().indexOf(code));
    if (code === self) return { ok: false, error: { message: `La fórmula no puede usar su propio resultado (${code}).`, position: at } };
    if (other.has(code)) return { ok: false, error: { message: `${code} no lleva valor (es un subtítulo o un documento).`, position: at } };
    if (!valued.has(code)) return { ok: false, error: { message: `No hay un analito ${code} en este estudio.`, position: at } };
  }
  return r;
}

/** A typed value as a formula input: "4.5", "4,5", "-2", " 60 " → number; "NEGATIVO", "4.5 %", "" → null. */
export function formulaNumber(raw: string | null | undefined): number | null {
  const t = (raw ?? "").trim();
  return /^[-+]?(\d+([.,]\d*)?|[.,]\d+)$/.test(t) ? Number(t.replace(",", ".")) : null;
}

/** Analyte kinds that carry no value and can never be a formula input. */
const NO_VALUE_KINDS = new Set(["subtitle", "document"]);

export interface FormulaAnalyte {
  id: string;
  code: string;
  kind: string;
  decimals: number | null;
  formula: string | null;
}

/** True for an analyte whose value the system computes instead of the chemist typing it. */
export function isCalculated(a: Pick<FormulaAnalyte, "kind" | "formula">): boolean {
  return a.kind === "numeric" && a.formula !== null && a.formula.trim() !== "";
}

/**
 * Every formula of a study checked together: syntax, sibling codes, and no circular reference
 * (BUN = URE / 2.1428 is fine; A = B + 1 with B = A * 2 is not). Keyed by analyte id.
 */
export function checkStudyFormulas(analytes: readonly FormulaAnalyte[]): Map<string, FormulaProblem> {
  const out = new Map<string, FormulaProblem>();
  const valueCodes = analytes.filter((a) => !NO_VALUE_KINDS.has(a.kind)).map((a) => a.code);
  const otherCodes = analytes.filter((a) => NO_VALUE_KINDS.has(a.kind)).map((a) => a.code);
  const parsed = new Map<string, ParsedFormula>();
  for (const a of analytes) {
    if (a.formula === null || a.formula.trim() === "") continue;
    if (a.kind !== "numeric") {
      out.set(a.id, { message: "Solo un analito numérico se calcula con fórmula.", position: 0 });
      continue;
    }
    const r = checkFormula(a.formula, { self: a.code, valueCodes, otherCodes });
    if (r.ok) parsed.set(formulaCodeKey(a.code), r.formula);
    else out.set(a.id, r.error);
  }
  for (const a of analytes) {
    const start = formulaCodeKey(a.code);
    if (!parsed.has(start)) continue;
    const path = findCycle(start, parsed);
    if (path) out.set(a.id, { message: `Referencia circular: ${path.join(" → ")}.`, position: 0 });
  }
  return out;
}

function findCycle(start: string, parsed: Map<string, ParsedFormula>): string[] | null {
  const walk = (code: string, path: string[]): string[] | null => {
    for (const dep of parsed.get(code)?.codes ?? []) {
      if (dep === start) return [...path, dep];
      if (path.includes(dep)) continue;
      const found = walk(dep, [...path, dep]);
      if (found) return found;
    }
    return null;
  };
  return walk(start, [start]);
}

/** Round half away from zero on the decimal digits (2.25 → 2.3, -2.25 → -2.3), like the report. */
export function roundTo(x: number, decimals: number): number {
  // Shift on the decimal string, not by multiplying: 1.005 × 100 is 100.49999… in binary.
  const s = String(Math.abs(x));
  const shifted = s.includes("e") ? Math.abs(x) * 10 ** decimals : Number(`${s}e${decimals}`);
  const r = Math.round(shifted) / 10 ** decimals;
  return r === 0 ? 0 : x < 0 ? -r : r;
}

/** Decimals used when the analyte has none set. */
export const DEFAULT_FORMULA_DECIMALS = 2;

export interface FormulaScope {
  /** The current value of an analyte code (upper-cased), or null when it has none. */
  value: (code: string) => number | null;
  patient: FormulaPatient;
}

export interface Evaluation {
  /** The unrounded result, or null when it cannot be computed. */
  value: number | null;
  /** Inputs (codes or patient variables) that had no value on the path taken. */
  missing: string[];
}

/** Walk the tree. Null for anything that is not a finite number; never throws. */
export function evaluateFormula(f: ParsedFormula, scope: FormulaScope): Evaluation {
  const missing: string[] = [];
  const miss = (name: string) => {
    if (!missing.includes(name)) missing.push(name);
    return null;
  };
  const fin = (x: number): number | null => (Number.isFinite(x) ? x : null);
  const ev = (n: Node): number | string | boolean | null => {
    switch (n.t) {
      case "num":
      case "str":
        return n.v;
      case "code": {
        const v = scope.value(n.code);
        return v === null || !Number.isFinite(v) ? miss(n.code) : v;
      }
      case "patient": {
        const v = scope.patient[n.name];
        return v === null ? miss(n.name) : v;
      }
      case "neg": {
        const x = ev(n.x) as number | null;
        return x === null ? null : -x;
      }
      case "bin": {
        const a = ev(n.a) as number | null;
        const b = ev(n.b) as number | null;
        if (a === null || b === null) return null;
        if (n.op === "+") return fin(a + b);
        if (n.op === "-") return fin(a - b);
        if (n.op === "*") return fin(a * b);
        if (n.op === "/") return b === 0 ? null : fin(a / b);
        return fin(a ** b);
      }
      case "cmp": {
        const a = ev(n.a);
        const b = ev(n.b);
        if (a === null || b === null) return null;
        if (n.op === "=") return a === b;
        if (n.op === "<>") return a !== b;
        if (n.op === "<") return a < b;
        if (n.op === "<=") return a <= b;
        if (n.op === ">") return a > b;
        return a >= b;
      }
      case "call": {
        if (n.fn === "si") {
          const c = ev(n.args[0]!);
          return c === null ? null : ev(c ? n.args[1]! : n.args[2]!);
        }
        const xs = n.args.map((a) => ev(a) as number | null);
        if (xs.some((x) => x === null)) return null;
        const [x, d] = xs as number[];
        switch (n.fn) {
          case "min":
            return Math.min(...(xs as number[]));
          case "max":
            return Math.max(...(xs as number[]));
          case "abs":
            return Math.abs(x!);
          case "redondear":
            return d === undefined ? roundTo(x!, 0) : Number.isInteger(d) && d >= 0 && d <= 6 ? roundTo(x!, d) : null;
          case "raiz":
            return x! < 0 ? null : Math.sqrt(x!);
          case "ln":
            return x! <= 0 ? null : Math.log(x!);
          case "log":
            return x! <= 0 ? null : Math.log10(x!);
          default:
            return fin(Math.exp(x!));
        }
      }
    }
  };
  const v = ev(f.ast) as number | null;
  return { value: v, missing };
}

export interface CalculatedValue {
  /** Rounded to the analyte's decimals, or null when it cannot be computed. */
  value: number | null;
  /** Codes or patient variables without a value; empty when the cause is arithmetic. */
  missing: string[];
  /** The formula is broken (should not happen: the catalogue checks it); es-MX. */
  error: string | null;
  /** The source, for the tooltip. */
  formula: string;
}

/** Completed years from the snapshot's age in days. */
export function ageYearsFromDays(ageDays: number | null | undefined): number | null {
  return ageDays === null || ageDays === undefined ? null : Math.floor(ageDays / 365.25);
}

/**
 * Compute every calculated analyte of a study from the other analytes' values. A formula may read
 * another calculated analyte (RAG = ALB / GLOB, GLOB = PT - ALB): it reads that analyte's ROUNDED
 * value, as the previous system did and as the report prints it. Keyed by analyte id.
 */
export function computeCalculated(analytes: readonly FormulaAnalyte[], typed: (code: string) => number | null, patient: FormulaPatient): Map<string, CalculatedValue> {
  const out = new Map<string, CalculatedValue>();
  const byCode = new Map(analytes.map((a) => [formulaCodeKey(a.code), a]));
  const problems = checkStudyFormulas(analytes);
  const results = new Map<string, CalculatedValue>();
  const resolve = (a: FormulaAnalyte): CalculatedValue => {
    const done = results.get(a.id);
    if (done) return done;
    const src = a.formula!.trim();
    const problem = problems.get(a.id);
    if (problem) {
      const r = { value: null, missing: [], error: problem.message, formula: src };
      results.set(a.id, r);
      return r;
    }
    // checkStudyFormulas parsed it already without a problem.
    const parsed = parseFormula(src) as Extract<ParseResult, { ok: true }>;
    const value = (code: string): number | null => {
      const dep = byCode.get(code)!;
      return isCalculated(dep) ? resolve(dep).value : typed(code);
    };
    const e = evaluateFormula(parsed.formula, { value, patient });
    // A calculated input without a value is reported by what IT lacks (RAG: "Falta PT", not GLOB).
    const missing = [...new Set(e.missing.flatMap((m) => { const dep = byCode.get(m); return dep && isCalculated(dep) ? resolve(dep).missing : [m]; }))];
    const r = { value: e.value === null ? null : roundTo(e.value, a.decimals ?? DEFAULT_FORMULA_DECIMALS), missing, error: null, formula: src };
    results.set(a.id, r);
    return r;
  };
  for (const a of analytes) if (isCalculated(a)) out.set(a.id, resolve(a));
  return out;
}

/** One line for the chemist under a calculated value that could not be computed. */
export function calculatedHint(c: CalculatedValue): string | null {
  if (c.value !== null) return null;
  if (c.error) return c.error;
  if (c.missing.length) return `Falta ${c.missing.map((m) => (m === "edad_anios" ? "la edad" : m === "sexo" ? "el sexo" : m)).join(", ")}`;
  return "No se puede calcular con estos valores (por ejemplo, una división entre cero).";
}

// ---- Formulas per branch (ADR 0032) -------------------------------------------------------------

/**
 * A branch's own formula for one analyte: at that branch it replaces the analyte's general
 * formula; `formula: null` means "at this branch it is captured, not calculated" (the analyser
 * there reports it). A branch without an entry inherits the general formula. Pilot case: BUN is
 * `URE / 2.1428` in general and captured at the branch whose analyser gives BUN, where URE is
 * `BUN * 2.1428` instead.
 */
export interface BranchFormula {
  branch_id: string;
  formula: string | null;
}

export type BranchFormulaAnalyte = FormulaAnalyte & { branch_formulas?: readonly BranchFormula[] | null };

/** The formula an analyte has at a branch (`null` branch = the general formula). Blank = none. */
export function formulaAtBranch(a: Pick<BranchFormulaAnalyte, "formula" | "branch_formulas">, branchId: string | null): string | null {
  const own = branchId === null ? undefined : (a.branch_formulas ?? []).find((b) => b.branch_id === branchId);
  const f = own ? own.formula : a.formula;
  return f === null || f.trim() === "" ? null : f.trim();
}

/** The study as it is calculated at a branch: every analyte with its effective formula there. */
export function studyAtBranch(analytes: readonly BranchFormulaAnalyte[], branchId: string | null): FormulaAnalyte[] {
  return analytes.map((a) => ({ id: a.id, code: a.code, kind: a.kind, decimals: a.decimals, formula: formulaAtBranch(a, branchId) }));
}

/** The effective sets a study has: the general one (`null`) and one per branch named by an override, in first-seen order. */
export function formulaBranches(analytes: readonly BranchFormulaAnalyte[]): (string | null)[] {
  const ids: (string | null)[] = [null];
  for (const a of analytes) for (const b of a.branch_formulas ?? []) if (!ids.includes(b.branch_id)) ids.push(b.branch_id);
  return ids;
}

export interface BranchFormulaProblem extends FormulaProblem {
  /** The branch whose effective set has the problem; null = the general formulas. */
  branch_id: string | null;
}

/**
 * `checkStudyFormulas` on every effective set of the study (ADR 0032): the general formulas, then
 * each branch that overrides one. So BUN = URE / 2.1428 in general with URE = BUN * 2.1428 at a
 * branch is refused unless that branch also captures BUN. Keyed by analyte id; the first problem
 * found wins (general first). A branch named twice for the same analyte is a problem too.
 */
export function checkStudyFormulasByBranch(analytes: readonly BranchFormulaAnalyte[]): Map<string, BranchFormulaProblem> {
  const out = new Map<string, BranchFormulaProblem>();
  for (const a of analytes) {
    const ids = (a.branch_formulas ?? []).map((b) => b.branch_id);
    const dup = ids.find((id, i) => ids.indexOf(id) !== i);
    if (dup !== undefined) out.set(a.id, { message: "Una sucursal aparece dos veces en las fórmulas por sucursal.", position: 0, branch_id: dup });
  }
  for (const branchId of formulaBranches(analytes)) {
    for (const [id, p] of checkStudyFormulas(studyAtBranch(analytes, branchId))) {
      if (!out.has(id)) out.set(id, { ...p, branch_id: branchId });
    }
  }
  return out;
}
